import { v4 as uuidv4 } from "uuid";
import styles from "./styles/index.module.scss";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { IBaseDocumentFilters, IDocumentCreatorFormProps } from "../../../@types/documents";
import BaseDocumentCreator, { IPrintButton } from "../../../components/documents/baseDocumentCreator";

import { IButton } from "../../../components/modalWindows/BaseModalWindow";
import { CheckBox } from "../../../components/controls/checkbox";
import { LotSelectorPanel } from "../../../components/lotSelector";
import FlexRow from "../../../components/controls/FlexRow";
import { ChangeableGrid, IChangeDataGrid } from "../../../components/grids/changeableGrid/ChangeableGrid";

import { InventoryVedItemCreatePluginSettings } from ".";

import GoodsSelectorInput from "../../../components/controls/inputs/GoodsSelectorInput";
import { ICostInfoDTO, IEntitySimpleDTO, ISummaryCostInfoDTO, IUserInfoDTO } from "../../../libs/coreapi-dto/@types/common";
import { InventoryVedItemFormDataType } from "./inventoryVedItemFormTab";
import { Accordion } from "../../../components/controls/accordion";
import { GridWrapper } from "../../../components/controls/GridWrapper";
import { TextInput, DateInput, TextAreaInput } from "../../../components/controls/inputs";
import { DictionaryInput } from "../../../components/controls/dictionaryInput";
import { ISelectorInputProps, StoreSelectorPanel } from "../../../components/storeSelectorPanel";

import useInventoryVedValidator from "./inventoryVedValidator";
import useGridFilter from "../../../system/hooks/useGridFilter";

import { useTabsContext } from "../../../system/providers/tabsProvider";
import { DateTime } from "luxon";
import PositionEditPanel, { IPanelEvent } from "../../../components/commandsPanels/PositionEditPanel";
import { IAppContext, useAppContext } from "../../../system/providers/appContextProvider";

import renderGlobalAlert from "../../../system/hooks/useGlobalAlert";
import KizScanModal from "../../Dictionaries/Kiz/ScanModal/KizScanModal";

import { countKizSum, controlKiz } from "../../../system/functions/controlKiz";
import { DocumentStateType, DocumentType, IdTableVariant } from "../../../@types/enumsGlobal";
import { validateDuplicateKizs } from "../../../system/functions/validateDuplicateKizs";
import { MoneyFormatComponent } from "../../../components/controls/inputs/BaseInput";
import { IKeyValuePair } from "../../Reports/Shared/Interfaces/IKeyValuePair";
import { checkLockStatus } from "../../CommonHelperFunctions";
import { useUserContext } from "../../../system/providers/userContextProvider";
import { DeleteModalWindow } from "../../../components/modalWindows/DeleteModalWindow";

import { onPharmacyChanged } from "../../../system/functions/onPharmacyChanged";
import { onStoreChanged } from "../../../system/functions/onStoreChanged";
import { checkStorePharmacy } from "../../../system/functions/checkStorePharmacy";

import { IInventoryVedEditDTO, IInventoryVedFillWithRemainedLotsDto, IInventoryVedGetDTO } from "../../../libs/coreapi-dto/documents/invoice/inventory/inventoryVed";
import { 
    IInventoryVedItemEditDTO, 
    IInventoryVedItemSaveStateDTO, 
    IInventoryVedItemViewDTO, 
    IInventoryVedSumReportDto 
} from "../../../libs/coreapi-dto/documents/invoice/inventory/inventoryVedItem";
import { IScalingRatioDTO } from "../../../libs/coreapi-dto/dirs/scalingRatio";
import { IKizBoxDTO, IKizDTO } from "../../../libs/coreapi-dto/dirs/kiz";

import { InventoryVedDataProvider } from "../../../Services/DataProviders/InventoryVedDataProvider";
import { ScalingRatioDataProvider } from "../../../Services/DataProviders/ScalingRatioDataProvider";
import { StoreDataProvider } from "../../../Services/DataProviders/StoreDataProvider";
import { Kiz2InventoryVedDataProvider } from "../../../Services/DataProviders/Kiz2InventoryVedDataProvider";
import { ConfirmationItemsModal } from "../../../components/modalWindows/ConfirmationItemsModal";
import { ProgressActionSpinner } from "../../../components/progressActionSpinner/ProgressActionSpinner";
import { useTranslation } from "react-i18next";
import { KizDataProvider } from "../../../Services/DataProviders/KizDataProvider";
import GridSelectorInput from "../../../components/controls/GridSelectorInput";
import UserLocalSelectorModal from "../../Service/UserLocal/components/selectorModal/UserLocalSelectorModal";
import checkLotInventoryAvailability, { ILotInventoryCheckResponse } from "../../../libs/core-api-requests/customRequest/checkLotInventoryAvailabilityRequest";
import { ILotDTO } from "../../../libs/coreapi-dto/accounting/lot";
import { useTimeout } from "../../../system/hooks/useTimeout";
import { MessageModalWindow } from "../../../components/modalWindows/MessageModalWindow";
import { promisifyMethod } from "../../../system/functions/promisifyMethod";
import { IStoreDTO } from "../../../libs/coreapi-dto/dirs/store";
import { NIL as uuidNull } from "uuid";
import FlexColumn from "../../../components/controls/FlexColumn";
import { useTourEducation } from "../../../components/tour/TourContext";
import { getInventoryVedFormTourSteps } from "../../../components/tour/TourSteps/InventoryVedFormTourSteps";

export type BtSaveOrOk = "ok" | "save";

interface IMessageModalProps {
    show: boolean;
    message: string;
    btSaveOrOk?: BtSaveOrOk;
}

export interface IInventoryVedFilters extends IBaseDocumentFilters {
    onlyLot: boolean;
    onlyLotRemain: boolean;
    onlyKiz: boolean;
}

const InventoryVedForm: FC<IDocumentCreatorFormProps<IInventoryVedGetDTO, IInventoryVedEditDTO>> = (props) => {
    const appContext = useAppContext();
    const tabsContext = useTabsContext();
    const currentIdTab = useRef<string>('');
    const { registerTour } = useTourEducation();
    const idTable = IdTableVariant.InventoryVed
    const userContext = useUserContext();
    const lockFromPermission = checkLockStatus(props.permission as IPermission, userContext.userPermission);
    const [setTimer, clearTimer]  = useTimeout();

    const [user, setUser] = useState<IEntitySimpleDTO | undefined>(() => {
        if (!props.document?.userName || !props.document?.idUserGlobal) {
            return undefined;
        }
        
        return {
            displayName: props.document.userName,
            idGlobal: props.document.idUserGlobal
        };
    });

    const [entity, setEntity] = useState<IInventoryVedEditDTO>({
        idGlobal: props.idGlobal as string,
        requiredScanKiz: props.document?.requiredScanKiz ?? false,
        documentDate: props.document?.documentDate ?? DateTime.now(),
        comment: props.document?.comment ?? "",
        idStoreGlobal: null,
        items: [], // Используется в документе как буфер строк
        store: props.document?.store,
        updateHeaderOnly: false,
        bulkClearKiz: false,
        user: user
    });

    const [storeFromLot, setStoreFromLot] = useState<IEntitySimpleDTO>();
    const [pharmacy, setPharmacy] = useState<IEntitySimpleDTO>();

    const validator = useInventoryVedValidator(entity);

    const storeDP = new StoreDataProvider(appContext.coreApiService);
    const scalingRatioDP = new ScalingRatioDataProvider(appContext.coreApiService);
    const inventoryVedDP = new InventoryVedDataProvider(appContext.coreApiService);
    const kiz2InventoryVedDP = new Kiz2InventoryVedDataProvider(appContext.coreApiService);
    const kizDP = new KizDataProvider(appContext.coreApiService);

    const pendingFilters = props.pendingFilters as IInventoryVedFilters | undefined;

    const [onlyLot, setOnlyLot] = useState<boolean>(true);
    const [onlyLotRemain, setOnlyLotRemain] = useState<boolean>(true);
    const [onlyKiz, setOnlyKiz] = useState<boolean>(false);

    const [showKizModal, setShowKizModal] = useState<boolean>(false);
    const [pricingModel, setPricingModel] = useState<IEntitySimpleDTO | null>(null);
    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({ show: false, message: "" });

    // Установить значение по IdGlobal для выделения строки
    const [selectById, setSelectById] = useState<string>()

    // Строки отображения в таблице
    const [viewItems, setViewItems] = useState<IInventoryVedItemViewDTO[]>([]);

    // Статус запроса обработки строк документа
    const [sendRequestDocumentItems, setSendRequestDocumentItems] = useState<boolean>(false);

    // Показывать верстку
    const [showForm, setShowForm] = useState<boolean>(false);

    // Локализация
    const { t } = useTranslation();
    const baseT = (value: string) => t("documents.inventoryVed." + value);
    const errorsT = (value: string) => t("errors." + value);

    // Применение изменений
    const applyChanges = (btType = "ok", applyControlKiz = true) => {

        // Контроль КИЗ для разных режимов сканирования
        if (validator.isValid() &&
            (!applyControlKiz || controlKiz(
                viewItems,
                () => setShowMessageModal({ message: errorsT("kizCountNotMatch"), show: true, btSaveOrOk: btType as any }),
                DocumentType.inventoryVed,
                {
                    requiredMode: entity.requiredScanKiz,
                    validationType: ""
                }
            ))) {

            const saveRequest = () => {

                // Установить для "пачки" строк статус save (save_state)
                if (props.variant === 'edit') {
                    inventoryVedDP.batchSaveState(props.idGlobal as string, { saveState: 'save' }, (result) => {

                        if (btType === "ok") {
                            props.ok({ ...entity, updateHeaderOnly: true });
                        } else {
                            props.save({ ...entity, updateHeaderOnly: true });
                        }
                    });
                }
            };

            // Если режим обязательного сканирования
            // валидация маркированных партий ведомости
            // старая логика
            // в новой валидация перенесена в режим отработки
            /*if (entity.requiredScanKiz) {

                const confirm = () => {
                    kiz2InventoryVedDP.confirmQuantityFromScannedKiz({ idDocument: props.idGlobal as string }, (result) => {

                        if (result) {
                            setModalWindow(<></>);
                            saveRequest();
                        } else {
                            renderGlobalAlert({ variant: "error", statusCode: 200, title: baseT("messages.quantityFactChangeError") });
                        }
                    })
                };

                allowDocumentItems(props.idGlobal as string, confirm).then((result) => {
                    if (result) {
                        saveRequest();
                    }
                });
            } else {
                saveRequest();
            }*/
            saveRequest();
        }
    };

    // Откат изменений
    const rollbackChanges = (action: () => void) => {

        // Удаляем из БД "пачку" строк со статусом autosave (save_state)
        if (props.variant === 'edit') {
            inventoryVedDP.deleteAutoSave(props.idGlobal as string, (result) => {
                action();
            });
        } else {
            action();
        }
    }

    const okHandler: IButton = { 
        onClick: () => applyChanges(),
        disabled: lockFromPermission,
        sendRequestGlobal: props.isSubmitting
    };

    const saveHandler: IButton = { 
        onClick: () => applyChanges("save"), 
        disabled: lockFromPermission,
        sendRequestGlobal: props.isSubmitting
    };

    const cancelHandler: IButton = { 
        onClick: () => rollbackChanges(() => props.cancel())
    };

    const printHandler: IPrintButton = {
        onClick: () => { return IdTableVariant.InventoryVed; },
        onSubItemClick: () => {
            let arr = new Array<IKeyValuePair>();
            if (props.document != undefined && props.document.mnemocode != undefined && props.idGlobal != undefined) arr.push(
                { 
                    key: props.idGlobal, 
                    value: props.document.mnemocode 
                });
            return arr;
        },
    };

    const otherActions: IButton[] = [
        {
            title: baseT("getRemnants"),//"Получить остатки"
            disabled: viewItems.length > 0 ||
                entity.idStoreGlobal === null ||
                props.document?.documentState === DocumentStateType.proc ||
                lockFromPermission,
            onClick: async () => fillWithRemainedLots(false)
        },
        {
            title: baseT("getKizMarkedRemnants"),//"Получить остатки, только маркированный товар"
            disabled: viewItems.length > 0 ||
                entity.idStoreGlobal === null ||
                props.document?.documentState === DocumentStateType.proc ||
                lockFromPermission,
            onClick: async () => fillWithRemainedLots(true)
        },
        {
            title: baseT("setAllQuantityRem"),//"Заполнить факт. кол-во документальным кол-вом"
            disabled: viewItems.length === 0 ||
                props.document?.documentState === DocumentStateType.proc ||
                lockFromPermission,
            onClick: () => setAllQuantityRem(),
        },
        { 
            title: baseT("deleteIncorrect"),//"Удалить ошибочные" 
            disabled: true/*viewItems.length === 0 ||
                props.document?.documentState === DocumentStateType.proc ||
                lockFromPermission*/,
            onClick: () => { }
        },
    ];

    const changeDataGridHandler = (cell: IChangeDataGrid) => {
        if (props.variant !== 'edit')
            return;

        if (cell.propertyName === "quantity") {
            const newQuantity = cell.value as number;

            const currentItem = viewItems.find(i => i.idGlobal === cell.idGlobal);

            if (!currentItem) {
                return;
            }

            setViewItems(prevItems =>
                prevItems.map(item => {
                    if (item.idGlobal === cell.idGlobal) {
                        const supplierSum = item.supplierPrice * newQuantity;
                        const supplierVatSum = item.supplierVatPrice * newQuantity;
                        const supplierSumIncVat = item.supplierPriceIncVat * newQuantity;

                        const retailSum = item.retailPrice * newQuantity;
                        const retailVatSum = item.retailVatPrice * newQuantity;
                        const retailSumIncVat = item.retailPriceIncVat * newQuantity;

                        return {
                            ...item,
                            quantity: newQuantity,
                            supplierSum,
                            supplierVatSum,
                            supplierSumIncVat,
                            retailSum,
                            retailVatSum,
                            retailSumIncVat
                        };
                    }
                    return item;
                })
            );

            const patchDocuments = [
                {
                    path: "/quantity",
                    op: "replace",
                    value: newQuantity
                },
                {
                    path: "/supplierCostInfo",
                    op: "replace",
                    value: {
                        vat: currentItem.supplierVat,
                        price: currentItem.supplierPrice,
                        vatPrice: currentItem.supplierVatPrice,
                        priceIncVat: currentItem.supplierPriceIncVat,
                        sum: currentItem.supplierPrice * newQuantity,
                        vatSum: currentItem.supplierVatPrice * newQuantity,
                        sumIncVat: currentItem.supplierPriceIncVat * newQuantity,
                        adprice: currentItem.supplierAdprice,
                    }
                },
                {
                    path: "/retailCostInfo",
                    op: "replace",
                    value: {
                        vat: currentItem.retailVat,
                        price: currentItem.retailPrice,
                        vatPrice: currentItem.retailVatPrice,
                        priceIncVat: currentItem.retailPriceIncVat,
                        sum: currentItem.retailPrice * newQuantity,
                        vatSum: currentItem.retailVatPrice * newQuantity,
                        sumIncVat: currentItem.retailPriceIncVat * newQuantity,
                        adprice: currentItem.retailAdprice,
                    }
                }
            ];

            inventoryVedDP.patchItem(props.idGlobal as string, cell.idGlobal, patchDocuments, (result) => {
                if (result) {
                    refreshItemsGrid();
                    refreshSumReport();
                } else {
                    renderGlobalAlert({ 
                        variant: "error", 
                        statusCode: 200, 
                        title: baseT("messages.saveItemError") 
                    });
                    refreshItemsGrid();
                }
            });
        }
    };

    //#region нетривиальные переменные

    const [itemsValidation, setItemsValidation] = useState<IValidationItem[]>([]);
    const [itemsFilter, setItemsFilter] = useState<IItemFilter[]>([]); // TODO: ?
    const [itemGridFilter, dispatchItemGridFilter] = useGridFilter();
    const [lotGridFilter, dispatchLotGridFilter] = useGridFilter();
    const [selectedItem, setSelectedItem] = useState<IInventoryVedItemViewDTO>();

    // Refs для target-элементов tour формы инвентаризационной ведомости.
    const inventoryVedRequiredFieldsTourRef = useRef<HTMLDivElement>(null);
    const inventoryVedOptionalFieldsTourRef = useRef<HTMLDivElement>(null);
    const inventoryVedRequiredScanKizTourRef = useRef<HTMLDivElement>(null);
    const inventoryVedCommentTourRef = useRef<HTMLDivElement>(null);
    const inventoryVedGoodsSelectionTourRef = useRef<HTMLDivElement>(null);
    const inventoryVedLotSelectionTourRef = useRef<HTMLDivElement>(null);
    const inventoryVedLotSearchOptionsTourRef = useRef<HTMLDivElement>(null);
    const inventoryVedSelectionSettingsTourRef = useRef<HTMLDivElement>(null);
    const inventoryVedItemsGridTourRef = useRef<HTMLDivElement>(null);
    const inventoryVedItemCommandsTourRef = useRef<HTMLDivElement>(null);
    const inventoryVedGridSearchTourRef = useRef<HTMLDivElement>(null);
    const inventoryVedTotalsAccordionTourRef = useRef<HTMLDivElement>(null);
    const inventoryVedAdditionalActionsTourRef = useRef<HTMLDivElement>(null);

    // Собираем сценарий tour формы инвентаризационной ведомости из отдельного файла шагов.
    const inventoryVedFormTourSteps = useMemo(() => getInventoryVedFormTourSteps({
        requiredFields: inventoryVedRequiredFieldsTourRef,
        optionalFields: inventoryVedOptionalFieldsTourRef,
        requiredScanKiz: inventoryVedRequiredScanKizTourRef,
        comment: inventoryVedCommentTourRef,
        goodsSelection: inventoryVedGoodsSelectionTourRef,
        lotSelection: inventoryVedLotSelectionTourRef,
        lotSearchOptions: inventoryVedLotSearchOptionsTourRef,
        selectionSettings: inventoryVedSelectionSettingsTourRef,
        itemsGrid: inventoryVedItemsGridTourRef,
        itemCommands: inventoryVedItemCommandsTourRef,
        gridSearch: inventoryVedGridSearchTourRef,
        totalsAccordion: inventoryVedTotalsAccordionTourRef,
        additionalActions: inventoryVedAdditionalActionsTourRef,
    }), []);

    // Общее число записей в таблице (строки ведомости)
    const [totalCount, setTotalCount] = useState(0);

    const [supplierCostInfo, setSupplierCostInfo] = useState<ISummaryCostInfoDTO>({} as ICostInfoDTO);
    const [retailCostInfo, setRetailCostInfo] = useState<ISummaryCostInfoDTO>({} as ICostInfoDTO);

    // Параметры для окна сканирования киз
    const [selectedItemEntity, setSelectedItemEntity] = useState<IInventoryVedItemEditDTO>();
    const [scalingRatio, setScalingRatio] = useState<IScalingRatioDTO>();

    // Состояние модального окна
    const [modalWindow, setModalWindow] = useState(<></>);

    //#endregion // нетривиальные переменные

    const blockedDocumentsMessage = "readyMessages.blockedDocuments";
    const reservedLotsDocumentsMessage = "readyMessages.reservedLotsDocuments";
    const reservedOpLotsDocumentsMessage = "readyMessages.reservedOpLotsDocuments";
    const openedCashSessionsMessage = "readyMessages.openedCashSessions";
    const savedInvoicesMessage = "readyMessages.savedInvoices";

    useEffect(() => {
        const init = () => {
            if (tabsContext?.currentTab) {
                currentIdTab.current = tabsContext.currentTab?.id;
            }
        };

        init();

        // Проверка готовности аптеки
        if (props.variant === "create") {
            renderProgressView(baseT("messages.drugstoreReadyProcessName"));
            inventoryVedDP.createCheck((result) => {
                setModalWindow(<></>);

                if (!result?.isAllowed) {

                    const detailItemsText = result?.items.join('\r\n')
                        .replace(savedInvoicesMessage, baseT(savedInvoicesMessage))
                        .replace(blockedDocumentsMessage, baseT(blockedDocumentsMessage))
                        .replace(reservedLotsDocumentsMessage, baseT(reservedLotsDocumentsMessage))
                        .replace(reservedOpLotsDocumentsMessage, baseT(reservedOpLotsDocumentsMessage))
                        .replace(openedCashSessionsMessage, baseT(openedCashSessionsMessage));

                    renderConfirmationModal(baseT(result?.title), baseT(result?.details),
                        detailItemsText, baseT(result?.question),
                        () => {
                            setModalWindow(<></>);
                            setShowForm(true);
                            init();
                        },
                        () => props.cancel());
                } else {
                    setShowForm(true);
                    init();
                }
            }, () => {
                props.cancel();
                setModalWindow(<></>);
                renderGlobalAlert({
                    variant: "error",
                    title: baseT("messages.drugstoreReadyCheckError"),//"Ошибка проверки готовности аптеки"
                    detail: baseT("messages.drugstoreReadyDetailsError"),//"Ошибка на стороне сервера"
                    instance: baseT("messages.drugstoreReadyProcessName"),
                    statusCode: 500,
                });
            });
        } else {
            setShowForm(true);
            init();
        }
    }, []);

    // Регистрируем tour, чтобы кнопка обучения в QuickAccessBar стала активной.
    useEffect(() => {
        return registerTour({
            plugin: "inventory_ved_create_plugin",
            tabId: currentIdTab.current,
            steps: inventoryVedFormTourSteps,
        });
    }, [registerTour, inventoryVedFormTourSteps]);

    useEffect(() => {
        if (entity.documentDate) {
            dispatchLotGridFilter({
                type: "addColumnFilter",
                payload: {
                    name: "dateDocument",
                    value: entity.documentDate.toFormat("yyyyMMdd"),
                    operator: "LessOrEq"
                }
            });
        } else {
            dispatchLotGridFilter({ type: "deleteColumnFilter", payload: "dateDocument" });
        }
    }, [entity.documentDate]);

    useEffect(() => {
        if (selectedItemEntity) {
            scalingRatioDP.overrideGetById(selectedItemEntity.idGoodsGlobal, selectedItemEntity.idScalingRatioGlobal, (value) => {
                setScalingRatio(value);
            });
        }
    }, [selectedItemEntity]);

    useEffect(() => {
        refreshSumReport();
    }, [props.variant]);

    useEffect(() => {
        if (pendingFilters) {
            setOnlyLot(pendingFilters.onlyLot);
            setOnlyLotRemain(pendingFilters.onlyLotRemain);
            setOnlyKiz(pendingFilters.onlyKiz);

            refreshItemsGrid();
        }
    }, [pendingFilters]);

    const refreshSumReport = () => {
        inventoryVedDP.getInventoryVedSumReport(props.idGlobal as string, (result) => {

            const report = result as IInventoryVedSumReportDto;

            setSupplierCostInfo({
                sum: report.sumSupplier,
                vatSum: report.sumVatSupplier,
                sumIncVat: report.sumIncVatSupplier,
            });
            setRetailCostInfo({
                sum: report.sumRetail,
                vatSum: report.sumVatRetail,
                sumIncVat: report.sumIncVatRetail,
            });
        });
    };

    const clearItemsBuffer = (targetEntity: IInventoryVedEditDTO) => {
        const len = targetEntity?.items?.length;
        targetEntity?.items?.splice(0, len);
    };

    // Сохранение нескольких партий в документ (для обработки массива)
    const saveLotsToDocument = (documentId: string, lots: ILotDTO[]) => {
        const refreshResult = () => {
            refreshItemsGrid();
            refreshSumReport();
        };

        // Сохраняем каждую позицию последовательно
        lots.forEach((lot) => {
            const newItem: IInventoryVedItemEditDTO = {
                idGlobal: uuidv4(),
                idLotGlobal: lot.idGlobal,
                idScalingRatioGlobal: lot.scalingRatio.idGlobal,
                idGoodsGlobal: lot.goods.idGlobal,
                quantity: 0,
                supplierCostInfo: lot.supplierCostInfo,
                retailCostInfo: lot.retailCostInfo,
                isKiz: lot.isKiz || false,
                kizs: [],
                kizBoxes: [],
                kizOsuDtos: lot.kizOsuDto !== undefined && lot.kizOsuDto !== null ? [ lot.kizOsuDto as IKizDTO ] : [],
                hasLot: true,
                gtinOsu: lot.gtinOsu,
                isOsu: lot.gtinOsu !== null && lot.gtinOsu !== undefined && lot.gtinOsu.length > 0 || lot.isPieceAccountingKizOsu, // // TODO: разделить признаки ОСУ и ПЭУ
                isPieceAccountingKizOsu: lot.isPieceAccountingKizOsu, // ПЭУ
                idSupplierGlobal: lot.idSupplierGlobal,
                isKizBox: lot.isKizBox || false,
                quantityRem: lot.quantityRem,
                internalBarcode: lot.internalBarcode,
                idSeriesGlobal: lot.idSeriesExternalGlobal,
                idRegCertGlobal: null,
                supplierDocNumber: null,
                supplierDocDate: null,
                supplierBillDocNumber: null,
                supplierBillDocDate: null,
                invoiceNum: null,
                invoiceDate: null,
                gtdNumber: lot.gtdNumber || '',
                registerPrice: lot.registerPrice,
                producerPrice: lot.producerPrice,
                idGosContractGoodsGlobal: lot.idGosContractGoodsExternalGlobal,
                dateCreated: DateTime.now(),
                numerator: lot.numerator,
                denominator: lot.denominator
            };

            inventoryVedDP.putItem(documentId, newItem, (result) => {
                if (result) {
                    refreshResult();
                } else {
                    renderGlobalAlert({ variant: "error", statusCode: 500, title: baseT("messages.saveItemError") });
                }
            });
        });
    };

    // Создание или добавление строки в документ
    const openCreateItemFormTab = (data: InventoryVedItemFormDataType) => {
        if (!data.pricing) {
            renderGlobalAlert({ variant: "error", statusCode: 404, title: baseT("messages.pricingModelForStoreError") });
        }

        tabsContext.openChild(
            "inventory_ved_item_create_plugin",
            "create",
            uuidv4(),
            (editItem, viewItem) => {
                if (data.type === 'lot')
                    setStoreFromLot(data.lot.store);

                const providePutRequest = (idDocumentGlobal: string = "") => {

                    const targetIdDocumentGlobal = (idDocumentGlobal.length > 0 ? idDocumentGlobal : props.idGlobal) as string;

                    // Функция обновления таблицы и состояния окна
                    // Если ведомость в состоянии создания и успешно сохранена
                    // Переход в режим редактирования
                    const refreshResult = () => {
                        if (props.variant === 'create')
                            props.redirect?.(targetIdDocumentGlobal, "edit",
                                {
                                    onlyLot,
                                    onlyLotRemain,
                                    onlyKiz
                                });
                        else {
                            refreshItemsGrid();
                            refreshSumReport();
                        }
                    };

                    const putItemRequest = () => {
                        inventoryVedDP.putItem(targetIdDocumentGlobal, editItem, (result) => {
                            if (result) {
                                
                                // Вода / KizOsu
                                if (editItem.kizOsuDto)
                                    kizDP.kizOsuAsync(editItem.idGlobal, editItem.kizOsuDto);

                                // Формируем буфер КИЗ если добавляли через сканирование в LotSelector
                                // bulkClear флаг регулируется через callback подтверждения KizScanModal
                                if (editItem.isKiz) {
                                    let targetItem = editItem as IInventoryVedItemEditDTO;
    
                                    const kizType = data.searchQuery?.type;
                                    switch (kizType) {
                                        case "kiz": {
                                            targetItem.kizs?.push(data.searchQuery?.data as IKizDTO);
                                            break;
                                        }
                                        case "kizBox": {
                                            targetItem.kizBoxes?.push(data.searchQuery?.data as IKizBoxDTO);
                                            break;
                                        }
                                        default: {
                                            break;
                                        }
                                    }
    
                                    // Добавляем строку с КИЗ в буфер
                                    entity.items?.push(targetItem);

                                    // Сохраняем ведомость
                                    // Если сканировали КИЗ
                                    let entityRequest = entity;
                                    if (data.skipHeaderValidation && data.type === 'lot') {
                                        entityRequest = { ...entity, idStoreGlobal: data.lot.store.idGlobal, store: data.lot.store}
                                    }

                                    if ((data.skipHeaderValidation || validator.isValid()) && kizType) {
                                        inventoryVedDP.update(entity.idGlobal as string, entityRequest, (e) => {
    
                                            // Очистка буфера в случае успешного сохранения
                                            // Предполагается очистка буфера в случаше ошибки
                                            // На данный момент callback в update должен отработать
                                            // TODO: учитывать failedCallback
                                            clearItemsBuffer(entity);
                                            refreshResult();
                                        });
                                    } else {

                                        // Обновление страницы с результатом
                                        // если добавляли без сканирования КИЗ
                                        refreshResult();
                                    }
                                } else {

                                    // Немаркированный товар
                                    refreshResult();
                                }
                            } else {
                                renderGlobalAlert({ variant: "error", statusCode: 500, title: baseT("messages.saveItemError") });
                            }
                        });
                    };

                    // Валидация КИЗ
                    if (editItem.isKiz && data.searchQuery?.data.barcode) {
                        allowKiz(targetIdDocumentGlobal, data.searchQuery?.data.barcode as string).then((result) => {
                            if (result) {
                                putItemRequest();
                            } else {
                                refreshResult();
                            }
                        });
                    } else {
                        putItemRequest();
                    }
                };

                // Создаем ведомость
                // затем прикрепляем к ней позицию
                if (props.variant === 'create') {

                    const targetEntity = !entity.idStoreGlobal && data.type === 'lot' ?
                        { ...entity, idStoreGlobal: data.lot.store.idGlobal } :
                        entity;

                    inventoryVedDP.create(targetEntity, (id) => {
                        providePutRequest(id);
                    });
                }

                // В режиме редактирования - update
                if (props.variant === 'edit') {
                    providePutRequest();
                }
            },
            data
        );
    };

    const contractorButton: ISelectorInputProps = {
        label: baseT("pharmacy"), //"Аптека"
        className: styles.width300px,
        disabled: viewItems.length > 0 || props.document?.documentState === DocumentStateType.proc || lockFromPermission,
        onSelect: (value) => {
            setPharmacy(value);
            dispatchLotGridFilter(onPharmacyChanged(value.idGlobal));
        },
        onClear: () => {
            setPharmacy(undefined);
            dispatchLotGridFilter(onPharmacyChanged(undefined));
        },
        treeViewCheckDirectoryType:'pharmacy'
    };

    const storeButton: ISelectorInputProps = {
        label: baseT("store"),//"Склад"
        value: entity.store ?? null,
        className: styles.width300px,
        disabled: viewItems.length > 0 || props.document?.documentState === DocumentStateType.proc || lockFromPermission,
        onSelect: (value) => {
            storeDP.getById(value.idGlobal, (e) => {
                if (e.pricingPricingModel) {
                    setPricingModel({ ...e.pricingPricingModel });
                } else {
                    renderGlobalAlert({ variant: "error", statusCode: 404, title: baseT("messages.pricingModelForStoreError") });
                }
            });
            setEntity({ ...entity, idStoreGlobal: value.idGlobal, store: value });
            dispatchLotGridFilter(onStoreChanged(value.idGlobal));
        },
        onClear: () => { 
            setEntity({ ...entity, idStoreGlobal: null, store: undefined });
            dispatchLotGridFilter(onStoreChanged());
        },
        error: validator.errors.idStoreGlobal,
        onFocus: () => validator.setErrors({ ...validator.errors, idStoreGlobal: undefined })
    };

    // Обновление таблицы с пагинацией для inventory_ved_items
    useEffect(() => {
        refreshItemsGrid();
    }, [itemGridFilter])

    const showKizModalButton: IPanelEvent = {
        disabled: !(selectedItem?.isKiz || selectedItem?.isOsu) || props.document?.documentState === DocumentStateType.proc || lockFromPermission,
        onClick: () => {

            // Подгрузка GetDTO для редактируемого элемента
            inventoryVedDP.getItemById(props.idGlobal as string, selectedItem?.idGlobal as string, (itemEntity) => {

                const extractKizOsuDtos = () => {
                    if (!itemEntity.isOsu) return
                    if ((itemEntity.kizOsuDtos === undefined || itemEntity.kizOsuDtos.length ===0) && itemEntity.kizOsuDto) return [ itemEntity.kizOsuDto ]
                    
                    return itemEntity.kizOsuDtos
                }
                    
                let itemEditDto = {
                    ...itemEntity,
                    idLotGlobal: itemEntity.lot?.idGlobal,
                    idGoodsGlobal: itemEntity.goods?.idGlobal,
                    idScalingRatioGlobal: itemEntity.scalingRatio?.idGlobal,
                    idGosContractGoodsGlobal: itemEntity.gosContractGoods?.idGlobal,
                    idSeriesGlobal: itemEntity.series?.idGlobal,
                    idRegCertGlobal: itemEntity.regCert?.idGlobal,
                    idSupplierGlobal: itemEntity.supplier?.idGlobal,
                    kizs: [],
                    kizBoxes: [],
                    kizOsuDto: itemEntity.kizOsuDto,
                    isOsu: itemEntity.isOsu,
                    kizOsuDtos: extractKizOsuDtos()
                } as IInventoryVedItemEditDTO

                // Обновить состояния
                setSelectedItemEntity(itemEditDto);
                setTimeout(() => setShowKizModal(true), 100);
            });
        }
    };

    const editItemButton: IPanelEvent = {
        disabled: selectedItem === undefined || props.document?.documentState === DocumentStateType.proc || lockFromPermission,
        onClick() {
            if (selectedItem) {

                // Подгрузка GetDTO для редактируемого элемента
                inventoryVedDP.getItemById(props.idGlobal as string, selectedItem.idGlobal, (itemEntity) => {
                    
                    const itemEditDto = {
                        ...itemEntity,
                        idLotGlobal: itemEntity.lot?.idGlobal,
                        idGoodsGlobal: itemEntity.goods?.idGlobal,
                        idScalingRatioGlobal: itemEntity.scalingRatio?.idGlobal,
                        idGosContractGoodsGlobal: itemEntity.gosContractGoods?.idGlobal === uuidNull ? null : itemEntity.gosContractGoods?.idGlobal,
                        idSeriesGlobal: itemEntity.series?.idGlobal,
                        idRegCertGlobal: itemEntity.regCert?.idGlobal,
                        idSupplierGlobal: itemEntity.supplier?.idGlobal
                    } as IInventoryVedItemEditDTO

                    tabsContext.openChild(
                        "inventory_ved_item_create_plugin",
                        "edit",
                        selectedItem?.idGlobal,
                        (editItem, viewItem) => {

                            inventoryVedDP.putItem(props.idGlobal as string, editItem, (result) => {
                                if (result) {

                                    refreshItemsGrid();
                                    refreshSumReport();
                                } else {

                                    renderGlobalAlert({ variant: "error", statusCode: 500, title: baseT("messages.saveError")/*"Ошибка сохранения ведомости"*/ });
                                }
                            });
                        },
                        {
                            type: "entity",
                            entity: itemEditDto,
                            pricing: pricingModel,
                            documentDate: entity.documentDate,
                            byLot: itemEditDto.hasLot
                        }
                    );
                });
            }
        },
    };

    const deleteItemButton: IPanelEvent = {
        disabled: selectedItem === undefined || props.document?.documentState === DocumentStateType.proc || lockFromPermission,
        onClick() {
            if (selectedItem) {
                const deleteItemFunc = () => {
                    inventoryVedDP.markItemDelete(props.idGlobal as string, selectedItem.idGlobal, (result) => {

                        if (result) {

                            // Обновить таблицу строк ведомостей
                            refreshItemsGrid();
                            refreshSumReport();
                        }
                    });
                };

                const modalTitle = `${selectedItem.displayName} ${baseT("messages.quantityAbbreviation")} ` +
                    `${selectedItem.quantity} ${baseT("messages.quantityRemnantAbbreviation")} ${selectedItem.quantityRem}`;
                renderDeleteModalWindow(modalTitle, null, null, deleteItemFunc);
            }
        },
    };

    const clearItemButton: IPanelEvent = {
        disabled: totalCount === 0 || props.document?.documentState === DocumentStateType.proc || lockFromPermission,
        onClick() {
            const provideDeleteAllRequest = () => {
                inventoryVedDP.deleteAll(props.idGlobal as string, (result) => {
                    if (result) {
                        refreshItemsGrid();
                        refreshSumReport();
                    }
                    else
                        renderGlobalAlert({ variant: "error", statusCode: 500, title: baseT("messages.clearError") });
                });
            }

            const customElement = <><br/><span>{baseT("messages.clearAlert")}<br/><b>{t("general.confirm")}</b></span></>;
            renderDeleteModalWindow(baseT("messages.clearProcessName"), "", customElement, provideDeleteAllRequest);
        },
    };

    const renderDeleteModalWindow = (entityTitle: string, customText: string | null, customElement: JSX.Element | string | null = null, deleteEntityFunc: any) => {
        setModalWindow(
            <DeleteModalWindow
                name={`${entityTitle} `}
                customText={customText}
                element={customElement as JSX.Element ?? <><br/><span><b>{entityTitle}</b>&nbsp;?</span></>}
                hideDefaultQuestionMark={true}
                cancel={{
                    onClick: () => {
                        setModalWindow(<></>); // TODO: viewState?
                    },
                }}
                delete={{
                    onClick: () => {
                        deleteEntityFunc();
                        setModalWindow(<></>);
                    },
                    title: t("general.delete") //"Удалить"
                }}
            />
        );
    };

    const renderProgressView = (actionMessage: string) => {
        setModalWindow(
            <ProgressActionSpinner
                loaderText={`${actionMessage}`}
            />
        );
    };

    const renderConfirmationModal = (header: string, warning: string, items: string, question: string, ok: (() => void) | undefined, cancel: () => void) => {
        setModalWindow(
            <ConfirmationItemsModal
                header={`${header}`}
                warning={ `${warning}` }
                listPositions={`${items}`}
                question={`${question}`}
                cancel={cancel}
                ok={ok}
                largeTextArea
            />
        );
    };

    // Выгрузка строк ведомости в таблицу с пагинацией
    const refreshItemsGrid = (scrollDirection: string = "", scrollCallback?: (verticalPivot: string, part: number) => void) => {
    
        if (props.idGlobal && props.variant === 'edit') {

            setSendRequestDocumentItems(true);

            let dataFilter = itemGridFilter;
            if (scrollDirection === "up" && dataFilter.pageNumber > 1)
                dataFilter.pageNumber--;
            if (scrollDirection === "down" && dataFilter.pageNumber === 2 && viewItems.length >= itemGridFilter.numberPerPage * 2)
                dataFilter.pageNumber++;

            inventoryVedDP.getItemsView(props.idGlobal, dataFilter, (items, totalCount) => {

                function extractCountKizScan(kizScannedCount) {
                    const match = kizScannedCount.match(/^\d+/);
                    return match ? parseInt(match[0]) : 0;
                }

                const itemsWithCalculatedSums = items.map(item => ({
                    ...item,
                    supplierSumIncVat: item.supplierPriceIncVat * item.quantity,
                    retailSumIncVat: item.retailPriceIncVat * item.quantity,
                    countKizScan: extractCountKizScan(item.kizScannedCount)
                }));

                if (scrollDirection === "") {
                    setViewItems(itemsWithCalculatedSums);
                }

                if (scrollDirection === "up") {
                    scrollCallback?.('height', 3);
                    if (viewItems.length >= itemGridFilter.numberPerPage * 2) {
                        scrollCallback?.('top', 2);
                        setViewItems([
                            ...itemsWithCalculatedSums.map((x) => ({ ...x, idRow: uuidv4() })), 
                            ...viewItems.slice(0, itemGridFilter.numberPerPage)
                        ]);
                    } else {
                        setViewItems([
                            ...itemsWithCalculatedSums.map((x) => ({ ...x, idRow: uuidv4() })), 
                            ...viewItems.slice(0, itemGridFilter.numberPerPage)
                        ]);
                    }
                }

                if (scrollDirection === "down") {
                    if (itemGridFilter.pageNumber <= Math.ceil(totalCount / itemGridFilter.numberPerPage)) {
                        if (viewItems.length >= itemGridFilter.numberPerPage * 2) {
                            scrollCallback?.('top', 2);
                            setViewItems([
                                ...viewItems.slice(itemGridFilter.numberPerPage), 
                                ...itemsWithCalculatedSums.map((x) => ({ ...x, idRow: uuidv4() }))
                            ]);
                        } else {
                            setViewItems([
                                ...viewItems, 
                                ...itemsWithCalculatedSums.map((x) => ({ ...x, idRow: uuidv4() }))
                            ]);
                        }
                    }
                }

                setTotalCount(totalCount);
                setSendRequestDocumentItems(false);
            });
        }
    }

    // Заполнить ведомость остатками
    // onlyKiz - заполнение только маркированным товаром
    const fillWithRemainedLots = (onlyKiz: boolean | false) => {

        const provideFillRequest = (id?: string) => {

            id = id ?? props.idGlobal as string

            if (entity.idStoreGlobal === null) {
                renderGlobalAlert({ variant: "error", statusCode: 404, title: baseT("messages.noStore") });
                return;
            }
    
            if (viewItems.length > 0) {
                renderGlobalAlert({ variant: "error", statusCode: 500, title: t("messages.remnantsLoadError") });
                return;
            }
    
            let lotHallmark: IInventoryVedFillWithRemainedLotsDto = {
                docDate: entity.documentDate,
                fillOnlyKiz: onlyKiz
            }
    
            inventoryVedDP.fillWithRemainedLots(id, entity.idStoreGlobal, lotHallmark, (result) => {
                if (result) {
                    if (props.variant === 'edit') {
                        refreshItemsGrid();
                    } else {
                        props.redirect?.(id as string, "edit", {
                            onlyLot,
                            onlyLotRemain,
                            onlyKiz
                        });
                    }
                } else {
                    if (props.variant === "create") {
                        props.redirect?.(id as string, "edit", {
                            onlyLot,
                            onlyLotRemain,
                            onlyKiz
                        });
                    }
                }
            });
        }

        // Если не задан мнемокод, сохраняем ведомость
        if (props.variant === 'create' || !props.document?.mnemocode || props.document?.mnemocode === '') {

            inventoryVedDP.create(entity, (id) => {
                provideFillRequest(id);
            }, 
            ()=> { }, 
            (e)=>{
                renderGlobalAlert({ variant: "error", statusCode: 0, title: baseT("messages.createError") });
            });
        } else {
            provideFillRequest();
        }
    }

    const setAllQuantityRem = () => {
        if (props.variant === 'edit') {
            inventoryVedDP.fillQuantityWithDocumentary(props.idGlobal as string, (result) => {
                if (result) {
                    refreshItemsGrid();
                    refreshSumReport();
                } else {
                    console.warn(baseT("messages.updateItemsError"));
                }
            });
        }
    };

    const allowKiz = (idDocument: string, barcode: string) => {
        return new Promise<boolean>((resolve, reject) => {

            kiz2InventoryVedDP.allowMarkings({ idDocument, barcode }, (result) => {

                if (!result?.isAllowed && result?.items?.length > 0) {
                    renderGlobalAlert({
                        variant: "warning",
                        title: result?.title,
                        detail: result?.details,
                        instance: baseT("messages.kizCodeAvailableProcessName"),
                        statusCode: 200,
                        isSound: true
                    });
                }

                resolve(result?.isAllowed);
            }, () => {
                // Отслеживание режима работы над документом в failedCallback
                // Потому что allowKiz используется при сканировании партии в добавлении в строку ведомости
                if (props.variant === "create") {
                    props.redirect?.(idDocument as string, "edit", {
                        onlyLot,
                        onlyLotRemain,
                        onlyKiz
                    });
                }
                resolve(false);
            })
        });
    };

    useEffect(() => {
        if (onlyLot && !onlyKiz) {
            dispatchLotGridFilter({ type: "deleteColumnFilter", payload: "isKiz" });
            
        } else if (onlyLot && onlyKiz) {
            dispatchLotGridFilter({
                type: "addColumnFilter",
                payload: {
                    name: "isKiz",
                    operator: "Eq",
                    value: true,
                }
            });
        }
    }, [onlyKiz]);

    useEffect(() => {
        setEntity(prev => ({
            ...prev,
            user
        }));
    }, [user]);

    return (
        <> { showForm && (
            <>
                {/* Передаем ref для tour на выпадающее меню "Действия". */}
                <BaseDocumentCreator ok={okHandler} save={saveHandler} otherMenuActions={otherActions} cancel={cancelHandler} print={printHandler} additionalActionsRef={inventoryVedAdditionalActionsTourRef}>
                <div className={styles.creatorWrapper}>
                    <div>
                        <FlexRow>
                            {/* Target для tour: обязательные поля ведомости. */}
                            <FlexColumn ref={inventoryVedRequiredFieldsTourRef}>
                                <FlexRow>
                                    <TextInput label={baseT("number")} className={styles.width300px} value={props.document?.mnemocode} disabled />
                                    <DateInput
                                        className={styles.width300px}
                                        required
                                        label={baseT("date")}
                                        value={entity.documentDate}
                                        onBlur={(value) => value && setEntity({ ...entity, documentDate: value })}
                                        error={validator.errors.idStoreGlobal}
                                        disabled={lockFromPermission || props.document?.documentState === DocumentStateType.proc}
                                    />
                                </FlexRow>
                                <FlexRow>
                                    <StoreSelectorPanel 
                                        we={props.document?.store ? false : true}
                                        storeFromLot={storeFromLot}
                                        compactMode={true}
                                        idTable={idTable}
                                        contractor={contractorButton}
                                        store={storeButton}
                                        notAutoComplete={props.variant === "edit" || props.variant === "copy"}
                                        documentStore={entity.store ?? undefined}
                                    />
                                </FlexRow>
                            </FlexColumn>
                            <FlexRow>
                                {/* Target для tour: необязательные поля ведомости. */}
                                <FlexColumn ref={inventoryVedOptionalFieldsTourRef}>
                                    <GridSelectorInput
                                        className={styles.width300px}
                                        disabled={false}
                                        selectedEntity={user}
                                        selectorModalJsx={UserLocalSelectorModal}
                                        id={"userGrid"}
                                        inline={false}
                                        label={baseT("user")}
                                        value={user?.displayName}
                                        onSelect={(entity) => {
                                            setUser(entity);
                                        }}
                                        onClear={() => {
                                            setUser(undefined)
                                        }}
                                    />
                                    <DictionaryInput label={baseT("storePlace")} className={styles.width300px} disabled />
                                </FlexColumn>
                                {/* Target для tour: настройка обязательного сканирования КИЗ. */}
                                <FlexColumn className={styles.marginTop20}>
                                    <div ref={inventoryVedRequiredScanKizTourRef}>
                                        <CheckBox
                                            id="requiredScanKiz"
                                            label={baseT("requiredScanKiz")}
                                            defaultChecked={entity.requiredScanKiz}
                                            onChanged={(checked: boolean) => setEntity({ ...entity, requiredScanKiz: checked })}
                                            disabled={lockFromPermission || props.document?.documentState === DocumentStateType.proc}
                                        />
                                    </div>
                                </FlexColumn>
                            </FlexRow>
                        </FlexRow>
                        {/* Target для tour: комментарий к ведомости. */}
                        <div ref={inventoryVedCommentTourRef}>
                            <Accordion opened={false} caption={baseT("comment")} id={`accordionAdditionally-${uuidv4()}`}>
                                <FlexRow>
                                    <TextAreaInput
                                        label={baseT("comment")}
                                        value={entity.comment}
                                        onEndChange={(value) => setEntity({ ...entity, comment: value })}
                                        className={styles.width100pc}
                                        disabled={lockFromPermission || props.document?.documentState === DocumentStateType.proc}
                                    />
                                </FlexRow>
                            </Accordion>
                        </div>
                        {/* Target для tour: дополнительные настройки подбора товаров. */}
                        <FlexRow className={styles.lotsFilterPanel} ref={inventoryVedSelectionSettingsTourRef}>
                            <CheckBox
                                id="onlyLot" label={baseT("onlyLot")}
                                defaultChecked={onlyLot}
                                onChanged={(checked: boolean) => {
                                    setOnlyLot(checked)
                                    !checked && setOnlyLotRemain(false)
                                }
                                }
                                disabled={lockFromPermission || props.document?.documentState === DocumentStateType.proc}
                            />
                            <CheckBox
                                id="onlyLotRemain"
                                label={baseT("onlyLotRemain")}
                                defaultChecked={onlyLotRemain}
                                onChanged={(checked: boolean) => {
                                    setOnlyLotRemain(checked)
                                }
                                }
                                disabled={lockFromPermission || props.document?.documentState === DocumentStateType.proc || !onlyLot}
                            />
                            <CheckBox
                                id="onlyKiz"
                                label={baseT("onlyKiz")}
                                defaultChecked={onlyKiz}
                                onChanged={(checked: boolean) => setOnlyKiz(checked)}
                                disabled={lockFromPermission || props.document?.documentState === DocumentStateType.proc}
                            />
                        </FlexRow>
                        <FlexRow>
                            <PositionEditPanel
                                kiz={showKizModalButton}
                                edit={editItemButton}
                                delete={deleteItemButton}
                                clear={clearItemButton}
                                // Target для tour: кнопки управления позициями ведомости.
                                tourRef={inventoryVedItemCommandsTourRef}
                            />
                            {onlyLot ? (
                                <LotSelectorPanel
                                    barcodeScan={tabsContext.currentTab && tabsContext.currentTab?.id === currentIdTab.current}
                                    onlyLotRemain={onlyLotRemain}
                                    gridFilter={lotGridFilter}
                                    dispatchGridFilter={dispatchLotGridFilter}
                                    onSelect={async (value, searchData) => {

                                        if (Array.isArray(value)) {
                                            // Фильтруем дубликаты по idGlobal
                                            const uniqueLots = value.filter((item, index, self) =>
                                                index === self.findIndex(t => t.idGlobal === item.idGlobal)
                                            );

                                            // В режиме создания - сначала создаем документ
                                            if (props.variant === 'create') {
                                                const firstLot = uniqueLots[0];
                                                if (!firstLot) return;

                                                // Заполнить склад для ведомости
                                                if (!entity.store || !entity.idStoreGlobal) {
                                                    setEntity({ ...entity, idStoreGlobal: firstLot.store.idGlobal, store: firstLot.store });
                                                }

                                                // Если МЦО не загружена, загрузить принудительно
                                                let storePricingModel = pricingModel;
                                                if (firstLot.store && !storePricingModel) {
                                                    const store: IStoreDTO = await promisifyMethod<IStoreDTO, typeof storeDP>(
                                                        storeDP,
                                                        storeDP.getById,
                                                        firstLot.store.idGlobal
                                                    )

                                                    storePricingModel = store.pricingPricingModel;

                                                    if (storePricingModel) {
                                                        setPricingModel({ ...store.pricingPricingModel });
                                                    } else {
                                                        renderGlobalAlert({ variant: "error", statusCode: 404, title: baseT("messages.pricingModelForStoreError") });
                                                        return;
                                                    }
                                                }

                                                // Создаем документ один раз
                                                const targetEntity = { ...entity, idStoreGlobal: firstLot.store.idGlobal };
                                                inventoryVedDP.create(targetEntity, (documentId) => {
                                                    // После создания документа, сохраняем все позиции
                                                    props.redirect?.(documentId, "edit", {
                                                        onlyLot,
                                                        onlyLotRemain,
                                                        onlyKiz
                                                    });
                                                    
                                                    // Сохраняем позиции после редиректа
                                                    setTimeout(() => {
                                                        saveLotsToDocument(documentId, uniqueLots);
                                                    }, 100);
                                                });
                                            } else {
                                                // В режиме редактирования - сохраняем позиции сразу
                                                saveLotsToDocument(props.idGlobal as string, uniqueLots);
                                            }

                                            return;
                                        }

                                        const selectedLot: ILotDTO = value;
                                        const inventoryCheckResponse: ILotInventoryCheckResponse = await checkLotInventoryAvailability(selectedLot.idGlobal);
                                        if (!inventoryCheckResponse.lotFreeForInventory) {
                                            renderConfirmationModal(
                                                selectedLot.displayName,
                                                "Ошибка подбора партии",
                                                inventoryCheckResponse.errMessage,
                                                "",
                                                undefined,
                                                () => setModalWindow(<></>))

                                            return;
                                        }

                                        if (!checkStorePharmacy(value, entity.store ?? null, pharmacy))
                                            return;

                                        // Заполнить склад для ведомости если не проставлен в ручную из лота
                                        if (!entity.store || !entity.idStoreGlobal) {
                                            setEntity({ ...entity, idStoreGlobal: value.store.idGlobal, store: value.store });
                                        }

                                        // Если МЦО не загружена, загрузить принудительно
                                        let storePricingModel = pricingModel;
                                        if (value && value.store && !storePricingModel) {
                                            const store: IStoreDTO = await promisifyMethod<IStoreDTO, typeof storeDP>(
                                                storeDP,
                                                storeDP.getById,
                                                value.store.idGlobal
                                            )

                                            storePricingModel = store.pricingPricingModel;

                                            if (storePricingModel) {
                                                setPricingModel({ ...store.pricingPricingModel });
                                            } else {
                                                renderGlobalAlert({ variant: "error", statusCode: 404, title: baseT("messages.pricingModelForStoreError") });
                                            }
                                        }

                                        openCreateItemFormTab({ type: "lot", lot: value, pricing: storePricingModel, searchQuery: searchData, skipHeaderValidation: true, documentDate: entity.documentDate })
                                    }}
                                    disabled={lockFromPermission || props.document?.documentState === DocumentStateType.proc}
                                    documentIdTable={idTable}
                                    activateGroupProcessing
                                    idStoreGlobal={entity.idStoreGlobal}
                                    // Target для tour внутри LotSelectorPanel.
                                    tourRefs={{
                                        selector: inventoryVedLotSelectionTourRef,
                                        searchOptions: inventoryVedLotSearchOptionsTourRef,
                                    }}
                                />
                            ) : (
                                // Target для tour: строка подбора товара.
                                <div ref={inventoryVedGoodsSelectionTourRef} className={styles.width100pc}>
                                    <GoodsSelectorInput
                                        className={styles.width100pc}
                                        label={baseT("searchGoods")}
                                        inline
                                        onSelect={(value) => openCreateItemFormTab({ type: "goods", goods: value, pricing: pricingModel, documentDate: entity.documentDate })}
                                        disabled={lockFromPermission || props.document?.documentState === DocumentStateType.proc}
                                    />
                                </div>
                            )}
                        </FlexRow>
                        
                        {/* Target для tour: таблица позиций ведомости. */}
                        <div ref={inventoryVedItemsGridTourRef}>
                            <ChangeableGrid
                                hiddenPagination={{ hiddenNumberPage: true, hiddenCountRow: true }} //show pagination
                                documentStateProc={lockFromPermission || props.document?.documentState === DocumentStateType.proc}
                                itemsFilter={itemsFilter}
                                itemsValidation={itemsValidation}
                                setItemsValidation={(value) => setItemsValidation(itemsValidation.map((item) => (item.idGlobal === value[0].idGlobal ? { ...item, ...value } : item)))}
                                gridId={uuidv4()}
                                documentType={DocumentType.inventoryVed}
                                data={viewItems}
                                onPageLoad={ refreshItemsGrid }
                                dispatchFilter={ dispatchItemGridFilter }
                                //autoSelect
                                filter={itemGridFilter}
                                totalCount={totalCount}
                                sendRequest={sendRequestDocumentItems}
                                onSelect={(row) =>
                                    setSelectedItem(viewItems.find((i) => i.idGlobal === row?.idGlobal))
                                }
                                //separator
                                autoSelectFirst
                                selectById={selectById}
                                plugin={InventoryVedItemCreatePluginSettings}
                                onDoubleClick={
                                    (row) => props.onDoubleClick?.(row)
                                }
                                onSort={
                                    (i) => dispatchItemGridFilter({
                                        type: "sort",
                                        payload: i.propertyName
                                    })
                                }
                                onFilterDelete={
                                    (i) => dispatchItemGridFilter(
                                        {
                                            type: "deleteColumnFilter", payload: i.propertyName
                                        })
                                }
                                onPageNumberChange={
                                    (n) => dispatchItemGridFilter({
                                        type: "changePageNumber",
                                        payload: { pageNumber: n }
                                    })
                                }
                                onNumberPerPageChange={
                                    (n) => dispatchItemGridFilter({
                                        type: "changeNumberPerPage",
                                        payload: { numberPerPage: n }
                                    })
                                }
                                onColumnEnter={(value: IChangeDataGrid) => clearTimer(() => changeDataGridHandler(value))}
                                onChangeData={(value: IChangeDataGrid) => setTimer(() => changeDataGridHandler(value))}
                                onBlur={(value: IChangeDataGrid) => clearTimer(() => changeDataGridHandler(value))}
                                searching
                                // Target для tour внутри ChangeableGrid: поиск по текущему документу.
                                tourRefs={{
                                    search: inventoryVedGridSearchTourRef,
                                }}
                                customKizControl={(item: IChangeableGridRow) => {
                                
                                    const requiredKizMode = entity.requiredScanKiz;
                                    const isDivided = item.numerator < item.denominator;
                                    const kizScanned = item.countKizScan;

                                    // Таблица истинности в виде guard clause
                                    const validationRules = [
                                        // Режим обязательного сканирования
                                        { condition: requiredKizMode && isDivided, result: kizScanned === 1 },
                                        { condition: requiredKizMode && !isDivided, result: item.isOsu || kizScanned === item.quantity },

                                        // Режим НЕобязательного сканирования
                                        { condition: !requiredKizMode && (isDivided || item.isOsu), result: true },
                                        {
                                            condition: !requiredKizMode && !isDivided && !item.isOsu, result:
                                                (item.documentalQuantity === item.quantity) || (kizScanned === item.quantity)
                                        }
                                    ];

                                    const result = validationRules.find(rule => rule.condition)?.result ?? false;
                                    return result;
                                }}
                            />
                        </div>
                    </div>
                    {/* Target для tour: блок сумм ведомости. */}
                    <div ref={inventoryVedTotalsAccordionTourRef}>
                        <Accordion opened={false} caption="" id={uuidv4()} bold>
                            <GridWrapper cols={3}>
                                <MoneyFormatComponent disabled label={baseT("sumReport.supplierCostSum")} value={supplierCostInfo.sum} />
                                <MoneyFormatComponent disabled label={baseT("sumReport.supplierCostVatSum")} value={supplierCostInfo.vatSum} />
                                <MoneyFormatComponent disabled label={baseT("sumReport.supplierCostSumIncVat")} value={supplierCostInfo.sumIncVat} />
                            </GridWrapper>
                            <GridWrapper cols={3}>
                                <MoneyFormatComponent disabled label={baseT("sumReport.retailCostSum")} value={retailCostInfo.sum} />
                                <MoneyFormatComponent disabled label={baseT("sumReport.retailCostVatSum")} value={retailCostInfo.vatSum} />
                                <MoneyFormatComponent disabled label={baseT("sumReport.retailCostSumIncVat")} value={retailCostInfo.sumIncVat} />
                            </GridWrapper>
                        </Accordion>
                    </div>
                </div>
                </BaseDocumentCreator>
            </>)}

            {showMessageModal.show && (
                <MessageModalWindow
                    message={showMessageModal.message}
                    ok={{
                        onClick: () => {
                            setShowMessageModal({ show: false, message: "" });
                            //showMessageModal.btSaveOrOk === "ok" ? props.ok({ ...entity, updateHeaderOnly: true }) : props.save({ ...entity, updateHeaderOnly: true });
                            applyChanges(showMessageModal.btSaveOrOk, false)
                        },
                    }}
                    cancel={{
                        onClick: () => {
                            setShowMessageModal({ show: false, message: "" });
                        },
                    }}
                    primary
                />
            )}

            {showKizModal && selectedItemEntity && (
                <KizScanModal
                    ok={(value: IInventoryVedItemEditDTO, scalingRatio?: IScalingRatioDTO) => {

                        // Update ведомости с полной перезаписью данных по КИЗ
                        let targetEntity: IInventoryVedEditDTO = { 
                            ...entity, 
                            items: [...entity.items.filter((i) => i.idGlobal !== value.idGlobal), value]
                        }
                        
                        setEntity(targetEntity);
                        setViewItems(
                            viewItems.map((x) => {
                                return {
                                    ...x,
                                    kizCount: x.idGlobal === value.idGlobal ? countKizSum(value) : x.kizCount,
                                };
                            })
                        );
                        setShowKizModal(false);
                        
                        // Сохранить буфер items в БД
                        // Действия с записями КИЗ на бэкенде
                        // Предполагается удаление записей если списки киз или коробов пустые
                        if (validator.isValid()) {

                            inventoryVedDP.update(targetEntity.idGlobal as string, {...targetEntity, bulkClearKiz: false}, (e) => {
                                clearItemsBuffer(targetEntity);
                                refreshItemsGrid();
                                refreshSumReport();
                            });
                        }
                    }}
                    cancel={() => setShowKizModal(false)}
                    selectedItem={selectedItemEntity}
                    documentType={DocumentType.inventoryVed}
                    document={{ idTable: IdTableVariant.InventoryVed, idDocument: entity.idGlobal as string }}
                    idLotFrom={selectedItemEntity?.idLotGlobal ?? null}
                    scalingRatio={scalingRatio}
                    numerator={scalingRatio?.numerator ?? 0}
                    idGoodsGlobal={selectedItemEntity.idGoodsGlobal}
                    onValidateDuplicate={(barcode) => validateDuplicateKizs(entity.items, barcode)}
                    onCustomAllow={allowKiz}
                />
            )}

            {modalWindow}
        </>
    );
};

export default InventoryVedForm;
